import type { AvailabilityQuery, AvailabilityResponse, Entity, FilterBy } from '../types'
import type { BookingState } from './state'
import { fetchAvailabilities } from '../api'
import { isCompleteZip } from '../validation'
import { nextNonSunday, todayCentral } from '../dates'

// ---------------------------------------------------------------------------
// The availability session: everything between the booking state and the
// /api/availabilities wire that isn't rendering. One session = one Selection
// (see CONTEXT.md); callers create a new session when selectionKey() changes,
// which is what invalidates every cache below — structurally, not via effect
// dependency arrays.
//
// Owned here: the Availability Window fetch with abort/race handling, the
// First Available derivation, and the today-anchored First Available request
// that backs the "First available" pill — issued only when the visible window
// can't answer for every entity, and cached per time-of-day filter so
// toggling Morning/Afternoon never refetches a map it already has.
//
// Not owned here: paging (start/history live in SlotPicker, mirrored to
// state.slotStart), the filter UI, analytics, rendering.
// ---------------------------------------------------------------------------

/** The query identity a session is bound to — everything but window + filter. */
export interface AvailabilitySelection {
  profile: number
  sub_profile: number | null
  status: 'new' | 'existing'
  region: number | null
  zip_code: string | null
  latitude: number | null
  longitude: number | null
  scheduling: 'provider' | null
}

/**
 * Derive the Selection from the booking state. Invariant (same as the step
 * itself): `state.profile` and `state.status` are non-null by the time the
 * slots step mounts. The ZIP is sent only once it's a full 5 digits (§2.2/§2.3).
 */
export function buildSelection(state: BookingState, providerBased: boolean): AvailabilitySelection {
  return {
    profile: state.profile!.id,
    sub_profile: state.subProfile ? state.subProfile.id : null,
    status: state.status!,
    region: state.region ? state.region.id : null,
    zip_code: state.zip && isCompleteZip(state.zip) ? state.zip : null,
    latitude: state.coords?.latitude ?? null,
    longitude: state.coords?.longitude ?? null,
    scheduling: providerBased ? 'provider' : null
  }
}

/** Stable identity for a Selection — memoize the session on this. */
export function selectionKey(sel: AvailabilitySelection): string {
  return [
    sel.profile,
    sel.sub_profile,
    sel.status,
    sel.region,
    sel.zip_code,
    sel.latitude,
    sel.longitude,
    sel.scheduling
  ].join('|')
}

export interface FirstSlot {
  date: string
  time: string
}

/** Earliest bookable slot in an entity's visible window, or null. */
export function firstAvailOf(entity: Entity): FirstSlot | null {
  const d = entity.dates.find((day) => day.times.length > 0)
  return d ? { date: d.date, time: d.times[0] } : null
}

export type FirstAvailStatus = 'pending' | 'ready' | 'failed'

export interface AvailabilityState {
  phase: 'loading' | 'error' | 'ready'
  /** The current window (when ready). */
  data: AvailabilityResponse | null
  /** Last good window — keeps the toolbar stable while the next one loads. */
  lastReady: AvailabilityResponse | null
  /** First Available per entity (`office_id-doctor_id`), today-anchored, for the active filter. */
  firstAvail: Record<string, FirstSlot>
  firstAvailStatus: FirstAvailStatus
}

export interface LoadArgs {
  start: string
  filterBy: FilterBy | null
  dateCount: number
}

export interface AvailabilitySession {
  load(args: LoadArgs): void
  getState(): AvailabilityState
  subscribe(listener: () => void): () => void
  dispose(): void
}

interface Deps {
  fetch?: typeof fetchAvailabilities
  /** Today's window start (next non-Sunday, Central). Injectable for tests. */
  todayStart?: () => string
}

export function createAvailabilitySession(
  selection: AvailabilitySelection,
  deps: Deps = {}
): AvailabilitySession {
  const doFetch = deps.fetch ?? fetchAvailabilities
  const todayStart = deps.todayStart ?? (() => nextNonSunday(todayCentral()))

  type FirstAvailEntry = { status: FirstAvailStatus; map: Record<string, FirstSlot> }
  // First Available, cached per time-of-day filter. Absent = never fetched;
  // 'failed' entries retry on the next load that needs them.
  const firstAvailByFilter = new Map<FilterBy | null, FirstAvailEntry>()

  let windowCtrl: AbortController | null = null
  let firstAvailCtrl: AbortController | null = null
  let activeFilter: FilterBy | null = null
  let lastReady: AvailabilityResponse | null = null
  const listeners = new Set<() => void>()

  let snapshot: AvailabilityState = {
    phase: 'loading',
    data: null,
    lastReady: null,
    firstAvail: {},
    firstAvailStatus: 'pending'
  }

  const emit = () => listeners.forEach((l) => l())
  const set = (patch: Partial<AvailabilityState>) => {
    snapshot = { ...snapshot, ...patch }
    emit()
  }

  const firstOfAll = (entities: Entity[]) => {
    const map: Record<string, FirstSlot> = {}
    for (const e of entities) {
      const f = firstAvailOf(e)
      if (f) map[`${e.office_id}-${e.doctor_id}`] = f
    }
    return map
  }

  const firstAvailView = (filterBy: FilterBy | null): Pick<AvailabilityState, 'firstAvail' | 'firstAvailStatus'> => {
    const entry = firstAvailByFilter.get(filterBy)
    return {
      firstAvail: entry?.status === 'ready' ? entry.map : {},
      firstAvailStatus: entry?.status ?? 'pending'
    }
  }

  const query = (start: string, dateCount: number, filterBy: FilterBy | null): AvailabilityQuery => ({
    start_date: start,
    date_count: dateCount,
    ...selection,
    filter_by: filterBy
  })

  /** Make sure First Available for this filter is (or becomes) known. */
  const ensureFirstAvail = (filterBy: FilterBy | null, windowStart: string, data: AvailabilityResponse) => {
    if (firstAvailByFilter.get(filterBy)?.status === 'ready') {
      set(firstAvailView(filterBy))
      return
    }
    // The common case: the window starts today and every entity has a slot in
    // it — First Available comes free with the window response.
    const today = todayStart()
    const answersEveryone =
      windowStart === today &&
      data.entities.length > 0 &&
      data.entities.every((e) => e.dates.some((day) => day.times.length > 0))
    if (answersEveryone) {
      firstAvailByFilter.set(filterBy, { status: 'ready', map: firstOfAll(data.entities) })
      set(firstAvailView(filterBy))
      return
    }
    firstAvailCtrl?.abort()
    const ctrl = new AbortController()
    firstAvailCtrl = ctrl
    doFetch(query(today, 7, filterBy), ctrl.signal)
      .then((base) => {
        firstAvailByFilter.set(filterBy, { status: 'ready', map: firstOfAll(base.entities) })
        if (filterBy === activeFilter) set(firstAvailView(filterBy))
      })
      .catch((e: unknown) => {
        if ((e as { name?: string })?.name === 'AbortError') return
        // Explicit failure state; cards fall back to the earliest slot in the
        // shown window (firstAvailOf) and the next load retries.
        firstAvailByFilter.set(filterBy, { status: 'failed', map: {} })
        if (filterBy === activeFilter) set(firstAvailView(filterBy))
      })
  }

  return {
    load({ start, filterBy, dateCount }: LoadArgs) {
      activeFilter = filterBy
      windowCtrl?.abort()
      const ctrl = new AbortController()
      windowCtrl = ctrl
      set({ phase: 'loading', data: null, lastReady, ...firstAvailView(filterBy) })

      doFetch(query(start, dateCount, filterBy), ctrl.signal)
        .then((data) => {
          if (ctrl.signal.aborted) return
          lastReady = data
          set({ phase: 'ready', data, lastReady })
          ensureFirstAvail(filterBy, start, data)
        })
        .catch((e: unknown) => {
          if ((e as { name?: string })?.name === 'AbortError') return
          set({ phase: 'error', data: null })
        })
    },
    getState: () => snapshot,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    dispose() {
      windowCtrl?.abort()
      firstAvailCtrl?.abort()
      listeners.clear()
    }
  }
}
